import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Menu, X, Search, Ticket, CalendarPlus, LayoutDashboard, LogOut, User, Settings, Shield } from 'lucide-react';

export const Navbar = () => {
  const { user, isAuthenticated, isOrganizer, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/events', label: 'Events' },
    { href: '/about', label: 'About' },
    { href: '/organizers', label: 'For Organizers' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (href) => {
    if (href === '/events' && location.pathname === '/events' && !location.search) return true;
    return location.pathname + location.search === href;
  };

  return (
    // paddingTop = safe-area-inset-top pushes the Navbar's h-16 content below
    // the iOS notch / status bar when viewport-fit=cover is set (public/index.html:5).
    // Without this, the CSS viewport extends into the notch and Navbar content
    // paints under the status bar. The Navbar's total rendered height becomes
    // 64px + safe-area-inset-top on notched devices — Layout.jsx <main> pt has
    // to match this so page content doesn't slide under the nav.
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 font-bold text-xl"
            data-testid="navbar-logo"
          >
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Ticket className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="hidden sm:inline">Next Gig</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive(link.href) ? 'text-primary' : 'text-muted-foreground'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <Link to="/events" className="hidden sm:flex">
              <Button variant="ghost" size="icon" data-testid="search-btn">
                <Search className="w-5 h-5" />
              </Button>
            </Link>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="relative h-10 w-10 rounded-full"
                    data-testid="user-menu-btn"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {(isOrganizer || isAdmin) && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="cursor-pointer" data-testid="menu-dashboard">
                          <LayoutDashboard className="w-4 h-4 mr-2" />
                          Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard/tickets" className="cursor-pointer" data-testid="menu-my-tickets">
                          <Ticket className="w-4 h-4 mr-2" />
                          My Tickets
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  {!isOrganizer && !isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/my-tickets" className="cursor-pointer" data-testid="menu-my-tickets">
                        <Ticket className="w-4 h-4 mr-2" />
                        My Tickets
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isOrganizer && (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard/events/create" className="cursor-pointer" data-testid="menu-create-event">
                        <CalendarPlus className="w-4 h-4 mr-2" />
                        Create Event
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isOrganizer && (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard/subscription" className="cursor-pointer" data-testid="menu-subscription">
                        <Ticket className="w-4 h-4 mr-2" />
                        Subscription
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer" data-testid="menu-admin">
                          <Shield className="w-4 h-4 mr-2" />
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={isOrganizer || isAdmin ? "/dashboard/settings" : "/settings"} className="cursor-pointer" data-testid="menu-settings">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="cursor-pointer text-destructive"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-btn">
                    Log in
                  </Button>
                </Link>
                <Link to="/register" className="hidden sm:block">
                  <Button className="btn-primary" data-testid="signup-btn">
                    Sign up
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border animate-fade-in">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.href) 
                      ? 'bg-primary/10 text-primary' 
                      : 'hover:bg-muted text-muted-foreground'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
